/**
 * Owns the lifecycle of `usePipelineDragDrop`'s optimistic stage overrides:
 * - clears pending safety timeouts on unmount
 * - clears an override once the server confirms the item is in the target
 *   stage
 * - clears an override whose target stage left the board (deleted stage /
 *   pipeline switch) instead of waiting for the 10s safety timeout
 *
 * Extracted from `usePipelineDragDrop` to keep that hook focused on drag
 * event handling. The refs passed in are the same objects the parent hook
 * uses in `handleDragStart`/`handleDragEnd` — they're threaded through
 * rather than duplicated, since both hooks must observe the same in-flight
 * request bookkeeping.
 */

import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';

import { logger } from '@/lib/logger';

interface UseOptimisticOverrideCleanupOptions<T> {
  /** Original items array */
  items: T[];
  /** Extract ID from an item */
  getItemId: (item: T) => string | number;
  /** Get current stage of an item */
  getItemStage: (item: T) => string;
  /**
   * The set of stage IDs the board currently renders. See
   * `usePipelineDragDrop`'s `validStageIds` for the full rationale.
   */
  validStageIds?: ReadonlySet<string>;
  /** Current optimistic stage overrides */
  optimisticUpdates: Map<string | number, string>;
  setOptimisticUpdates: Dispatch<SetStateAction<Map<string | number, string>>>;
  clearTimeoutsRef: RefObject<
    Map<string | number, ReturnType<typeof setTimeout>>
  >;
  inFlightByItemRef: RefObject<Map<string | number, number>>;
  requestGenerationRef: RefObject<Map<string | number, number>>;
}

export function useOptimisticOverrideCleanup<T>({
  items,
  getItemId,
  getItemStage,
  validStageIds,
  optimisticUpdates,
  setOptimisticUpdates,
  clearTimeoutsRef,
  inFlightByItemRef,
  requestGenerationRef,
}: UseOptimisticOverrideCleanupOptions<T>): void {
  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = clearTimeoutsRef.current;
    return () => {
      for (const timeout of timeouts.values()) {
        clearTimeout(timeout);
      }
    };
  }, [clearTimeoutsRef]);

  // Ref to read optimisticUpdates without adding it as an effect dependency
  const optimisticUpdatesRef = useRef(optimisticUpdates);
  optimisticUpdatesRef.current = optimisticUpdates;

  // Reconcile optimistic updates against server data.
  // When server confirms an item is in the target stage, clear the optimistic
  // override immediately. This prevents the "replay" bug where stale server
  // data causes a snap-back when the timeout fires before the server has
  // projected the events.
  useEffect(() => {
    const updates = optimisticUpdatesRef.current;
    if (updates.size === 0) {
      return;
    }

    const confirmedIds: (string | number)[] = [];
    for (const [itemId, optimisticStage] of updates) {
      const serverItem = items.find(item => getItemId(item) === itemId);
      if (serverItem && getItemStage(serverItem) === optimisticStage) {
        confirmedIds.push(itemId);
      }
    }

    if (confirmedIds.length === 0) {
      return;
    }

    // Cancel timeouts before updating state (side effects should be outside setState)
    for (const id of confirmedIds) {
      const timeout = clearTimeoutsRef.current.get(id);
      if (timeout) {
        clearTimeout(timeout);
        clearTimeoutsRef.current.delete(id);
      }
      // Retire the request generation now that the confirmed override is being
      // cleared, otherwise requestGenerationRef accumulates one entry per moved
      // item on a long-lived board. Only retire it when no request for this
      // item is still in flight: a slow stale request may have captured a
      // generation value, and resetting the counter would let a later move
      // reuse it. When a request IS in flight, that request's own cleanup path
      // retires the entry once it settles.
      if (!inFlightByItemRef.current.get(id)) {
        requestGenerationRef.current.delete(id);
      }
    }

    logger.debug('pipeline-dnd: server confirmed optimistic moves', {
      confirmedIds: confirmedIds.map(String),
    });

    setOptimisticUpdates(prev => {
      const next = new Map(prev);
      for (const id of confirmedIds) {
        next.delete(id);
      }
      return next;
    });
  }, [
    items,
    getItemId,
    getItemStage,
    clearTimeoutsRef,
    inFlightByItemRef,
    requestGenerationRef,
    setOptimisticUpdates,
  ]);

  // Proactively clear overrides whose target stage left the board (stage
  // deleted / pipeline switched while a move was in flight). The render-time
  // filter in `optimisticItems` (in usePipelineDragDrop) already keeps the
  // item visible at its server stage; this effect just retires the dead
  // override and its timer instead of letting the 10s safety timeout do it.
  // Depends on `optimisticUpdates` (not just `validStageIds`) so it also runs
  // when a NEW override is added targeting a stage that is already absent from
  // the board — that case doesn't change `validStageIds`, so a deps list of
  // `[validStageIds]` alone would leave the stale override to linger until the
  // 10s timeout (and be applied if the stage ID were reintroduced).
  useEffect(() => {
    if (!validStageIds || optimisticUpdates.size === 0) {
      return;
    }
    const invalid = [...optimisticUpdates].filter(
      ([, stage]) => !validStageIds.has(stage)
    );
    if (invalid.length === 0) {
      return;
    }
    for (const [id] of invalid) {
      const timeout = clearTimeoutsRef.current.get(id);
      if (timeout) {
        clearTimeout(timeout);
        clearTimeoutsRef.current.delete(id);
      }
      // Retire the generation together with the dead override so the map does
      // not leak entries for items whose target stage left the board — but only
      // when no request for this item is still in flight (see the reconciliation
      // effect above for why a live request must keep the counter intact).
      if (!inFlightByItemRef.current.get(id)) {
        requestGenerationRef.current.delete(id);
      }
    }
    logger.warn(
      'pipeline-dnd: clearing optimistic overrides targeting removed stages',
      {
        overrides: invalid.map(([id, stage]) => ({
          itemId: String(id),
          targetStage: stage,
        })),
      }
    );
    setOptimisticUpdates(prev => {
      const next = new Map(prev);
      for (const [id] of invalid) {
        next.delete(id);
      }
      return next;
    });
  }, [
    validStageIds,
    optimisticUpdates,
    clearTimeoutsRef,
    inFlightByItemRef,
    requestGenerationRef,
    setOptimisticUpdates,
  ]);
}
